import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  getChatAttachmentUploadUrl,
  uploadChatAttachmentToS3,
} from '@/services/aws';
import type { ChatMessageAttachment } from '@/slices/chat/chatSlice';
import type { ChatTurnAttachment } from '@/types/chatStream';
import { capture } from '@/utils/analytics';
import {
  ChatImageTranscodeError,
  isAcceptedChatImageType,
  MAX_CHAT_IMAGE_BYTES,
  MAX_CHAT_IMAGES,
  transcodeChatImage,
} from '@/utils/imageTranscode';

export interface PendingAttachment {
  localId: string;
  file: File;
  previewUrl: string;
  status: 'uploading' | 'ready' | 'error';
  key?: string;
  mimeType?: string;
  sizeBytes?: number;
  errorMessage?: string;
}

export type PendingAttachmentMethod = 'picker' | 'paste' | 'drop';

interface PendingAttachmentOptions {
  disabled: boolean;
  notify: (message: string) => void;
  onFirstAttach: () => void;
}

/** Draft files never enter Redux. On send, ownership of ready previews passes to the live message. */
export const usePendingAttachments = (options: PendingAttachmentOptions) => {
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const pendingRef = useRef(pending);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const reminded = useRef(false);

  const update = useCallback((next: PendingAttachment[]) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  const upload = useCallback(
    async (attachment: PendingAttachment, method: PendingAttachmentMethod) => {
      const exists = () =>
        pendingRef.current.some((item) => item.localId === attachment.localId);

      const patch = (fields: Partial<PendingAttachment>) =>
        update(
          pendingRef.current.map((item) =>
            item.localId === attachment.localId ? { ...item, ...fields } : item
          )
        );

      try {
        const image = await transcodeChatImage(attachment.file);
        if (!exists()) return;
        const previewUrl = URL.createObjectURL(image.blob);
        URL.revokeObjectURL(attachment.previewUrl);
        patch({ previewUrl });

        const data = await getChatAttachmentUploadUrl({
          mimeType: image.mimeType,
          sizeBytes: image.sizeBytes,
        });

        if (!exists()) return;
        if (!data?.uploadUrl || !data.key)
          throw new Error('Unable to prepare image upload');

        await uploadChatAttachmentToS3(
          data.uploadUrl,
          image.blob,
          image.mimeType
        );

        if (!exists()) return;

        patch({
          status: 'ready',
          key: data.key,
          mimeType: image.mimeType,
          sizeBytes: image.sizeBytes,
          errorMessage: undefined,
        });

        capture('chat_image_attached', {
          method,
          count: pendingRef.current.length,
          mimeType: image.mimeType,
          sizeBytes: image.sizeBytes,
          wasTranscoded: true,
        });
      } catch (error) {
        if (!exists()) return;

        const errorMessage =
          error instanceof ChatImageTranscodeError
            ? error.message
            : 'Ora could not upload this image. Try again.';

        patch({ status: 'error', errorMessage });
        optionsRef.current.notify(errorMessage);
      }
    },
    [update]
  );

  const add = useCallback(
    (files: FileList | File[], method: PendingAttachmentMethod = 'picker') => {
      if (optionsRef.current.disabled || Capacitor.isNativePlatform()) return;

      const accepted = Array.from(files).filter((file) => {
        if (!isAcceptedChatImageType(file)) {
          optionsRef.current.notify(
            'Please attach only PNG, JPEG, WebP, HEIC, or HEIF images.'
          );

          return false;
        }
        if (file.size > MAX_CHAT_IMAGE_BYTES) {
          optionsRef.current.notify('Each image must be 10 MB or smaller.');
          return false;
        }
        return true;
      });

      const available = MAX_CHAT_IMAGES - pendingRef.current.length;
      if (accepted.length > available)
        optionsRef.current.notify('You can attach up to 4 images.');

      const additions = accepted
        .slice(0, available)
        .map((file): PendingAttachment => ({
          file,
          localId: crypto.randomUUID(),
          previewUrl: URL.createObjectURL(file),
          status: 'uploading',
        }));

      if (!additions.length) return;
      if (!reminded.current) {
        reminded.current = true;
        optionsRef.current.onFirstAttach();
      }
      update([...pendingRef.current, ...additions]);

      additions.forEach((item) => {
        void upload(item, method);
      });
    },
    [update, upload]
  );

  const remove = useCallback(
    (localId: string) => {
      const attachment = pendingRef.current.find(
        (item) => item.localId === localId
      );

      if (attachment) URL.revokeObjectURL(attachment.previewUrl);
      update(pendingRef.current.filter((item) => item.localId !== localId));
    },
    [update]
  );

  const retry = useCallback(
    (localId: string) => {
      if (optionsRef.current.disabled || Capacitor.isNativePlatform()) return;

      const attachment = pendingRef.current.find(
        (item) => item.localId === localId
      );

      if (attachment?.status !== 'error') return;

      update(
        pendingRef.current.map((item) =>
          item.localId === localId
            ? { ...item, status: 'uploading', errorMessage: undefined }
            : item
        )
      );

      void upload(attachment, 'picker');
    },
    [update, upload]
  );

  const clear = useCallback(
    (preservedUrls = new Set<string>()) => {
      pendingRef.current.forEach((item) => {
        if (!preservedUrls.has(item.previewUrl))
          URL.revokeObjectURL(item.previewUrl);
      });

      update([]);
    },
    [update]
  );

  useEffect(
    () => () => {
      pendingRef.current.forEach((item) =>
        URL.revokeObjectURL(item.previewUrl)
      );

      pendingRef.current = [];
    },
    []
  );

  const readyRefs: Array<ChatMessageAttachment & ChatTurnAttachment> =
    pending.flatMap((item) =>
      item.status === 'ready' &&
      item.key &&
      item.mimeType &&
      item.sizeBytes !== undefined
        ? [
            {
              key: item.key,
              mimeType: item.mimeType,
              sizeBytes: item.sizeBytes,
              previewUrl: item.previewUrl,
            },
          ]
        : []
    );

  return {
    pending,
    readyRefs,
    isUploading: pending.some((item) => item.status === 'uploading'),
    add,
    remove,
    retry,
    clear,
  };
};
